import { Endgame, RunOutcome } from "@highmarch/app";
import { SECONDS_PER_DAY, eventEndsAt, starCount } from "@highmarch/domain";
import { fmtCompact } from "../ui/kit";
import { icon } from "../ui/iconsKit";
import { toast } from "../ui/toast";
import { showEvents, fmtDay, fmtLeft } from "../ui/hub/eventsSheet";
import { emberGlyph, goldWorthAt, paidLine, setRewardGoldWorth } from "../ui/hub/eventRewards";
import { earnHow, eventArtHtml, eventArtUrl, themeClass } from "../ui/hub/eventLook";
import { econCue } from "../ui/hub/purchaseFx";
import { rowHtml } from "../ui/components";
import { paintedIconHtml } from "../ui/featureIcons";
import { trophyGainRowHtml } from "../ui/trophy/trophyGain";
import { setEventsPageBanner } from "../ui/hub/eventsPage";
import { evBannerHtml, evChipHtml } from "../ui/hub/evKit";
import { EndScreenFacts, Feature, HubApi, MORE_ORDER } from "./registry";

/** Where the endgame's end-screen block sits: after the chest, before the Chapter 7 card. */
const END_ORDER = 20, CHAPTER7_ORDER = 90;
/** The painted icon at the head of each end-screen row (CSS px). */
const END_ICON_PX = 28;
/** Run outcomes kept for end-screen redraws (a run's block reads the same however often it is drawn). */
const OUTCOMES_KEPT = 4;
/** Debug: embers one tap on the debug row adds. */
const DEBUG_EMBERS = 100;
/** `?evday=YYYY-MM-DD` (debug only) moves the events' clock to noon UTC that day: captures and the smoke test inside an event. */
const DEBUG_DAY_PARAM = "evday";
const NOON_SECONDS = SECONDS_PER_DAY / 2;
/** The event's picture in the hub chip's disc (CSS px). */
const CHIP_ART_PX = 28;
/** Space between the hub's event chip and the top bar above it. */
const CHIP_GAP_PX = 8;
/** The end screen's milestone cue waits for the win/lose stinger to land first (ms). */
const EVENT_CUE_DELAY_MS = 900;

/** The events' clock: the game's, or with `?evday` (debug) shifted to that day. */
export function eventClock(now: () => number, debug: boolean, search: string): () => number {
  const day = debug ? new URLSearchParams(search).get(DEBUG_DAY_PARAM) : null;
  const at = day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? Date.parse(`${day}T00:00:00Z`) / 1000 + NOON_SECONDS : NaN;
  if (!Number.isFinite(at)) return now;
  const shift = at - now();
  return () => now() + shift;
}

/** A run's outcome as the end block reads it (`eventMarks` absent = none: older callers and tests). */
export type EndOutcome = Omit<RunOutcome, "eventMarks"> & { readonly eventMarks?: number };

/**
 * The end screen's endgame block: which run this was, the embers it paid, a Heroic star, the Trial's reward, and the
 * Marks the run earned for the Event shop (docs/SHOP.md §9.3; Valor until 2026-09-30: the Trial, a first Heroic win, a farm win). A fact, not an
 * offer: nothing on the end screen opens a shop.
 */
export function endBlockHtml(o: EndOutcome, facts: Pick<EndScreenFacts, "level" | "won">, embersNow: number, nextAt: number | null): string {
  const rows: string[] = [];
  // Compact cream rows (UI design system v2, end.css `.erow`): painted icon, the headline, a line, a note at the end.
  const row = (kind: string, lead: string, title: string, sub: string, end = ""): string =>
    rowHtml({ lead, title, sub, end, compact: true, className: `erow ${kind}` });
  const medal = paintedIconHtml("achievementGold", END_ICON_PX);
  if (o.kind === "heroic") rows.push(o.heroicFirst
    ? row("heroic", medal, "HEROIC STAR", `Level ${facts.level} · +${fmtCompact(o.heroicMarks)} Marks`, `<small class="short">+${fmtCompact(o.heroicMarks)}</small>`)
    : row("heroic", medal, "HEROIC", facts.won ? "Won again: the star was already yours" : "RETRY plays it on Heroic again"));
  if (o.kind === "trial") rows.push(o.trialReward
    ? row("trial", paintedIconHtml("marks", END_ICON_PX), "TRIAL WON", paidLine(o.trialReward, o.gear, false))
    : row("trial", paintedIconHtml("marks", END_ICON_PX), "WEEKLY TRIAL", facts.won ? "Already won this week" : "RETRY plays the Trial again"));
  // MP-23: the Trophies a first Heroic win and the Trial's first win paid, in a row of their own (their lines leave them out)
  const trophies = (o.heroicTrophies ?? 0) + (o.trialReward?.trophies ?? 0);
  if (trophies > 0) rows.push(trophyGainRowHtml(trophies, END_ICON_PX));
  if ((o.eventMarks ?? 0) > 0) rows.push(row("marks gain", paintedIconHtml("marks", END_ICON_PX), `+${fmtCompact(o.eventMarks ?? 0)}`, "Marks", `<small>${o.kind === null ? "farm win" : "for the Event shop"}</small>`));
  if (o.event && o.embers > 0) rows.push(row("embers gain", emberGlyph(END_ICON_PX), `+${o.embers}`, `${o.event.name} ${o.event.currency.toLowerCase()}`, `<small>${nextAt === null ? `${fmtCompact(embersNow)} · track complete` : `${fmtCompact(embersNow)} / ${fmtCompact(nextAt)}`}</small>`));
  // `stack`: a short screen puts the rows side by side, but the Trial's reward line needs a row of its own.
  return rows.length > 0 ? `<div class="evend${o.kind === "trial" && o.trialReward ? " stack" : ""}">${rows.join("")}</div>` : "";
}

/** After level 60 (D10): the campaign's end card. */
export function chapter7Html(): string {
  return `<div class="ch7 k-card plate"><div class="seal">${icon("lock", 26)}</div><div class="t"><small class="disp">CAMPAIGN COMPLETE</small><b class="disp">Chapter 7 — coming soon</b><span>The Highmarch holds. New lands are on the way. Until then: Heroic tiers, the Weekly Trial and events.</span></div></div>`;
}

/**
 * Events and the endgame (FINISH_PLAN WP6): More → Events (the live event's free track, the Weekly Trial, the Heroic
 * tier) with a red count for rewards to claim and a new event not yet opened; the end screen's block (embers, the
 * Heroic star, the Trial's reward) and the Chapter 7 card after level 60; a Guide topic; lines on the away card.
 */
export const eventsFeature: Feature = {
  id: "events",
  register(ctx, registry) {
    const endgame = new Endgame(ctx.catalog);
    setRewardGoldWorth(goldWorthAt(ctx.catalog.balance, () => ctx.catalog.highestLevelCleared));
    const now = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
    // Weekly event weeks long over leave the save (their "seen" ids and progress): it would grow by one a week.
    if (ctx.save.seenEvents !== undefined) ctx.save.seenEvents = endgame.pruneEnded(now(), ctx.save.seenEvents);
    else endgame.pruneEnded(now(), []);
    const unseen = (): boolean => { const e = endgame.live(now()); return e !== null && endgame.open && !(ctx.save.seenEvents ?? []).includes(e.id); };
    // Orders, upgrades and Tribute since the last look pay the live event's sources (MP-21) before anything counts claims.
    const claimable = (): number => { endgame.syncHub(now()); return endgame.claimable(now()); };
    const openSheet = (hub: HubApi): void => {
      // Opening the sheet is the look: the running event stops counting as new.
      const e = endgame.live(now());
      if (e && unseen()) ctx.save.seenEvents = [...new Set([...(ctx.save.seenEvents ?? []), e.id])];
      endgame.syncHub(now());
      ctx.persist();
      showEvents(hub.parent, { catalog: ctx.catalog, endgame, now, onChange: () => hub.changed(), startRun: (level, m) => registry.startRun(level, m), panels: registry.eventPanels() });
    };

    registry.moreRow({
      id: "events", order: MORE_ORDER.events, icon: "eventEmber", home: "events",
      label: () => "Events",
      note: () => {
        const t = now(), live = endgame.live(t), soon = live ? null : endgame.upcoming(t);
        if (live) return `${live.name} · ${fmtLeft(eventEndsAt(live) - t)} left`;
        const trial = endgame.trial(t);
        if (trial) return `Trial · ${trial.modifier.name}`;
        return soon ? `${soon.name} · ${fmtDay(soon.startDay)}` : "Trials & Heroic";
      },
      dot: () => claimable() + (unseen() ? 1 : 0),
      open: (hub) => openSheet(hub),
    });

    // The Events page opens on the live event's bright banner (UI pass 2026-10-21); a tap opens the event.
    setEventsPageBanner(() => {
      const t = now(), e = endgame.live(t);
      if (!e || !endgame.open) return "";
      const chips = `${evChipHtml(`${icon("clock", 13)}${fmtLeft(eventEndsAt(e) - t)}`)}${evChipHtml(`${emberGlyph(16)}<b>${fmtCompact(endgame.progress.embersOf(e.id))}</b>`)}`;
      return evBannerHtml({ scene: "event", title: e.name, chips, art: eventArtHtml(e, 46), dot: claimable() + (unseen() ? 1 : 0), tap: true, className: `evpagebnr ${themeClass(e)}` });
    });

    // The hub's event chip: while an event runs, under the commander — its name, time left, embers and a red count.
    registry.hubWidget({
      id: "eventChip",
      mount: (hub, layer) => {
        const chip = document.createElement("button");
        chip.className = "evhubchip";
        chip.onclick = () => openSheet(hub);
        layer.append(chip);
        const update = (): void => {
          const t = now(), e = endgame.live(t);
          chip.hidden = e === null || !endgame.open;
          if (!e || chip.hidden) return;
          const dot = claimable() + (unseen() ? 1 : 0);
          // MP-21: each week's kind tints the chip and shows its own picture, so a new week reads as new.
          chip.className = `evhubchip ${themeClass(e)}`;
          chip.innerHTML = `<span class="ic">${eventArtHtml(e, CHIP_ART_PX)}</span><span class="tx"><b class="disp">${e.name}</b><small>${icon("clock", 11)} ${fmtLeft(eventEndsAt(e) - t)} · ${emberGlyph(11)} ${fmtCompact(endgame.progress.embersOf(e.id))}</small></span>${dot > 0 ? `<i class="dot">${dot}</i>` : ""}`;
        };
        // Under the top bar (which wraps when features add pills). The chips' one layout goes top-down: this chip only
        // follows the bar, and the builder/Tribute pair on the fortress moves below it (ui/hub/tributeChip.ts layout).
        const place = (): void => {
          if (chip.hidden) return;
          const bar = layer.parentElement?.querySelector<HTMLElement>(".topbar")?.getBoundingClientRect();
          chip.style.top = `${Math.round((bar?.bottom ?? 0) + CHIP_GAP_PX)}px`;
          // The Deals button (features/deals.ts) holds the left column under the commander: the chip starts after it.
          const deals = layer.parentElement?.querySelector<HTMLElement>('.pills > .fpill[data-pill="deals"]')?.getBoundingClientRect();
          chip.style.left = deals && deals.width > 0 ? `${Math.round(deals.right + CHIP_GAP_PX)}px` : "";
        };
        update(); place();
        return { update: () => { update(); place(); }, layout: place, dispose: () => chip.remove() };
      },
    });

    registry.moreRow({
      id: "eventsDebug", order: MORE_ORDER.debug + 2, icon: "flame", home: "events",
      label: () => `Debug: +${DEBUG_EMBERS} embers`,
      note: () => { const e = endgame.live(now()); return e ? `${endgame.progress.embersOf(e.id)} ${e.currency.toLowerCase()}` : "no event live"; },
      visible: () => ctx.debug && endgame.live(now()) !== null,
      open: (hub) => {
        const e = endgame.live(now());
        if (!e) return;
        endgame.progress.creditBattle(e, `debug:${endgame.progress.embersOf(e.id)}:${Date.now()}`, DEBUG_EMBERS);
        hub.changed();
        toast(hub.parent, `${endgame.progress.embersOf(e.id)} ${e.currency.toLowerCase()}`);
      },
    });

    // The end screen: a run's outcome is settled once (embers, the Heroic star, the Trial's reward) and drawn from the cache.
    const outcomes = new Map<string, RunOutcome>();
    const cued = new WeakSet<RunOutcome>();
    const outcomeFor = (f: EndScreenFacts): RunOutcome => {
      // MP-21: Race Week's runs share the week's seed, so an event run's key also carries its length
      const key = `${f.level}:${f.sim.seed}:${f.won ? "w" : "l"}${f.sim.stagesCleared}${f.sim.isEventRun ? `:${f.sim.tick}` : ""}`;
      let o = outcomes.get(key);
      if (!o) {
        // MP-21: what the run did, for the live event's sources — its stars, leaks, rares and the battle's tally (kills per
        // enemy, elites, early calls; read before the level is left: registry.battleCounts).
        o = endgame.finishRun({
          level: f.level, won: f.won, stagesCleared: f.sim.stagesCleared, modifiers: f.sim.modifiers, key, now: now(),
          stars: starCount(f.stars), leaks: f.sim.leaks, counts: registry.battleCounts(), rareKills: (f.sim.rareRuns ?? []).filter((r) => r.outcome === "killed").length,
        });
        // A farm win (a plain won replay of a level cleared before this run — `firstClear` was fixed when the run
        // began; the catalog already holds this run's own clear, banked before the end screen draws: M33) pays the
        // Event shop's farm Marks (Valor until 2026-09-30), up to shops.json farmWinsPerDay a UTC day.
        if (f.won && o.kind === null && !f.sim.firstClear && !f.sim.isIron && !f.sim.isDaily && !f.sim.isEventRun && !f.sim.isChallenge) {   // Iron, Daily Road and the event modes pay their own (features/modes.ts, dailyRoad.ts, eventModes.ts); a challenge run pays none (MP-28)
          const farm = ctx.catalog.shops.farmWin(now(), `farm.${key}`);
          if (farm > 0) o = { ...o, eventMarks: o.eventMarks + farm };
        }
        outcomes.set(key, o);
        if (outcomes.size > OUTCOMES_KEPT) outcomes.delete(outcomes.keys().next().value!);
        ctx.persist();
      }
      return o;
    };
    registry.endScreenExtra({
      id: "events", order: END_ORDER,
      html: (f) => {
        const o = outcomeFor(f);
        const have = o.event ? endgame.progress.embersOf(o.event.id) : 0;
        return endBlockHtml(o, f, have, o.event?.nodes.find((n) => n.embers > have)?.embers ?? null);
      },
      // The run's embers reached a new reward on the track: the event cue, once per run (the screen can redraw).
      bind: (_block, f) => {
        const o = outcomeFor(f);
        if (!o.event || o.embers <= 0 || cued.has(o)) return;
        cued.add(o);
        const have = endgame.progress.embersOf(o.event.id), before = have - o.embers;
        if (o.event.nodes.some((n) => n.embers > before && n.embers <= have)) window.setTimeout(() => econCue("event"), EVENT_CUE_DELAY_MS);
      },
    });
    registry.endScreenExtra({ id: "chapter7", order: CHAPTER7_ORDER, html: (f) => (f.won && f.level === ctx.catalog.levelCount ? chapter7Html() : "") });

    registry.guideTopic({
      id: "events", group: "castle", label: "Events & Trials", art: { icon: "flame" }, fromLevel: ctx.balance.events.unlockAfterLevel + 1, steps: [],
      lines: [
        "A new **event** every week, each earning embers its own way: Events shows how. Claim every reward on the track.",
        "The **Weekly Trial** replays a level you've cleared with a twist. Win it once a week for Marks and a chest.",
        "Earn all **3 stars** on a level to open its **Heroic** tier: tougher raiders, a Heroic star and Marks.",
      ],
    });

    // Only on a real return (awaySeconds known): back from a battle the chip's dot and the More badge already say it,
    // and the card would otherwise pop on every hub entry while an event runs.
    registry.awayItems(({ awaySeconds }) => {
      const e = endgame.live(now());
      if (awaySeconds === null || !e || !endgame.open) return [];
      const ready = claimable();
      const pic = eventArtUrl(e) ?? "flame";                   // the event's own picture (MP-21 art)
      if (unseen()) return [{ icon: pic, title: `${e.name} has begun`, detail: `${earnHow(ctx.balance, e)} for ${e.currency.toLowerCase()} · the Events tab` }];
      return ready > 0 ? [{ icon: pic, title: `${ready} event reward${ready === 1 ? "" : "s"} to claim`, detail: `${e.name} · the Events tab` }] : [];
    });
  },
};
